import {
  Stack, Text, Card, Group, Badge, Code, Anchor, Center, Loader, Table, Alert,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { IconUpload, IconFile, IconX, IconCheck } from '@tabler/icons-react';
import type { ImportLog } from '@taksikortti/shared';
import { useImportsList, uploadImport, type UploadImportResponse } from '../api/imports';
import { ApiError } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { t } from '../lib/strings';
import { formatDateTime } from '../lib/dates';

const STALE_MS = 30 * 60 * 1000;

function ImportStatusBadge({ row }: { row: ImportLog }) {
  if (row.finishedAt != null) {
    return <Badge color="green" variant="light">{t.import.statusDone}</Badge>;
  }
  const ageMs = Date.now() - new Date(row.startedAt).getTime();
  if (ageMs >= STALE_MS) {
    return <Badge color="gray" variant="light">{t.import.statusInterrupted}</Badge>;
  }
  const total = row.totalRows ?? 0;
  const pct = total > 0 ? Math.min(100, Math.floor(((row.processedRows ?? 0) / total) * 100)) : null;
  return (
    <Badge color="blue" variant="light">
      {pct == null ? `${t.import.statusRunning}…` : `${t.import.statusRunning} ${pct} %`}
    </Badge>
  );
}

export function ImportPage() {
  const importsList = useImportsList();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<UploadImportResponse | null>(null);
  const [headerError, setHeaderError] = useState<{ missing: string[]; unexpected: string[] } | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  async function onDrop(files: File[]): Promise<void> {
    const file = files[0];
    if (!file) return;
    setUploaded(null);
    setHeaderError(null);
    setGenericError(null);
    setPendingName(file.name);
    setUploading(true);
    try {
      const r = await uploadImport(file);
      setUploaded(r);
      qc.invalidateQueries({ queryKey: ['imports'] });
      qc.invalidateQueries({ queryKey: ['cases'] });
      qc.invalidateQueries({ queryKey: ['duplicates'] });
    } catch (err) {
      if (err instanceof ApiError && err.body.code === 'header_error') {
        setHeaderError({
          missing: (err.body.missing as string[]) ?? [],
          unexpected: (err.body.unexpected as string[]) ?? [],
        });
      } else {
        setGenericError(
          err instanceof ApiError ? (err.body.message ?? err.message) : ((err as Error)?.message ?? t.errors.generic),
        );
      }
    } finally {
      setUploading(false);
      setPendingName(null);
    }
  }

  return (
    <Stack gap="md">
      <PageHeader title={t.import.title} />

      <Card withBorder>
        <Stack gap="xs">
          <Text fw={500} size="sm">{t.import.columnsHelpTitle}</Text>
          <Group gap={6}>
            {t.import.requiredColumns.map((col) => (
              <Code key={col}>{col}</Code>
            ))}
          </Group>
          <Text size="xs" c="dimmed">{t.import.columnsHelpHint}</Text>
        </Stack>
      </Card>

      <Dropzone
        onDrop={(files) => { void onDrop(files); }}
        onReject={() => notifications.show({ color: 'red', message: t.errors.fileRejected })}
        accept={{ 'text/csv': ['.csv'] }}
        maxFiles={1}
        maxSize={50 * 1024 * 1024}
        loading={uploading}
      >
        <Group justify="center" gap="md" mih={120} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept><IconUpload size={32} /></Dropzone.Accept>
          <Dropzone.Reject><IconX size={32} /></Dropzone.Reject>
          <Dropzone.Idle><IconFile size={32} /></Dropzone.Idle>
          <Stack gap={0} align="center">
            <Text size="lg">{t.import.drop}</Text>
            <Text size="sm" c="dimmed">CSV · max 50 MB</Text>
          </Stack>
        </Group>
      </Dropzone>

      {uploading && (
        <Card withBorder bg="blue.0">
          <Group gap="sm" align="center">
            <Loader size="sm" />
            <Text fw={600} c="blue.9">{t.import.uploading}</Text>
            {pendingName && <Text size="sm" c="blue.8">{pendingName}</Text>}
          </Group>
        </Card>
      )}

      {headerError && (
        <Card withBorder bg="red.0">
          <Stack gap="xs">
            <Text c="red.8" fw={600}>{t.import.headerErrorTitle}</Text>
            {headerError.missing.length > 0 && (
              <Stack gap={4}>
                <Text c="red.8" size="sm">{t.import.headerErrorMissing}</Text>
                <Group gap={6}>
                  {headerError.missing.map((col) => (
                    <Code key={col} color="red">{col}</Code>
                  ))}
                </Group>
              </Stack>
            )}
            {headerError.unexpected.length > 0 && (
              <Stack gap={4}>
                <Text c="red.8" size="sm">{t.import.headerErrorUnexpected}</Text>
                <Group gap={6}>
                  {headerError.unexpected.map((col) => (
                    <Code key={col}>{col}</Code>
                  ))}
                </Group>
              </Stack>
            )}
            <Text c="red.8" size="xs">{t.import.headerErrorHint}</Text>
          </Stack>
        </Card>
      )}

      {genericError && !headerError && (
        <Card withBorder bg="red.0">
          <Stack gap="xs">
            <Text c="red.8" fw={600}>{t.import.errorTitle}</Text>
            <Text c="red.8" size="sm">{genericError}</Text>
            <Text c="red.8" size="xs">{t.import.errorHint}</Text>
          </Stack>
        </Card>
      )}

      {uploaded && (
        <Alert icon={<IconCheck size={20} />} color="green" variant="light" title={t.import.uploadedTitle}>
          <Stack gap={4}>
            <Text size="sm">{t.import.uploadedBackground}</Text>
            <Group gap="xs">
              <Text size="xs" c="dimmed">{uploaded.totalRows.toLocaleString('fi-FI')} riviä</Text>
              <Code>{uploaded.importId}</Code>
            </Group>
          </Stack>
        </Alert>
      )}

      <Card withBorder>
        <Stack gap="sm">
          <Text fw={500}>{t.import.recent}</Text>
          {importsList.isLoading && <Center p="md"><Loader size="sm" /></Center>}
          {importsList.data && importsList.data.rows.length === 0 && (
            <Text size="sm" c="dimmed">Ei tuonteja tässä kuussa.</Text>
          )}
          {importsList.data && importsList.data.rows.length > 0 && (
            <Table.ScrollContainer minWidth={900}>
              <Table fz="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Aika</Table.Th>
                    <Table.Th>Tiedosto</Table.Th>
                    <Table.Th>Tila</Table.Th>
                    <Table.Th ta="right">Uusia</Table.Th>
                    <Table.Th ta="right">Aiemmin tuotuja</Table.Th>
                    <Table.Th ta="right">Päivittyneitä</Table.Th>
                    <Table.Th ta="right">Virheitä</Table.Th>
                    <Table.Th ta="right">Uusia tapauksia</Table.Th>
                    <Table.Th>Käyttäjä</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {importsList.data.rows.map((r: ImportLog) => (
                    <Table.Tr key={r.importId}>
                      <Table.Td>{formatDateTime(r.startedAt)}</Table.Td>
                      <Table.Td><Code>{r.fileName}</Code></Table.Td>
                      <Table.Td><ImportStatusBadge row={r} /></Table.Td>
                      <Table.Td ta="right">{r.importedCount}</Table.Td>
                      <Table.Td ta="right">{r.skippedCount}</Table.Td>
                      <Table.Td ta="right">{(r.updatedCount ?? 0) > 0 ? r.updatedCount : '—'}</Table.Td>
                      <Table.Td ta="right">{r.failedCount}</Table.Td>
                      <Table.Td ta="right">
                        {r.newCasesCount > 0
                          ? <Badge color="blue" variant="light">{r.newCasesCount}</Badge>
                          : '—'}
                      </Table.Td>
                      <Table.Td>{r.who}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          {importsList.data && importsList.data.rows.length > 0 && (
            <Anchor component={Link} to="/cases" size="sm">Avaa tapaukset →</Anchor>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
